import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { generateAssessmentPdf } from './generateAssessmentPdf';

export const createHandlers = ({
  selectedGoals,
  setSelectedGoals,
  setConfirmDialogOpen,
  setPendingAction,
  setDateRange,
  setSelectedStudent,
  setSelectedPatient,
  setGoalType,
  setSelectedAssessmentType,
  goalType,
  selectedAssessmentType,
  navigate,
  setPdfModalOpen,
    setPdfUrl
}) => {
  const handleDateChange = (range) => {
    if (selectedGoals.length > 0) {
      setConfirmDialogOpen(true);
    } else {
      setDateRange(range || [null, null]);
    }
  };

  const handleSelectStudent = (value) => {
    if (selectedGoals.length > 0) {
      setPendingAction({ type: 'setSelectedStudent', value });
      setConfirmDialogOpen(true);
    } else {
      setSelectedStudent(value);
    }
  };

  const handleSelectPatient = (value) => {
    if (selectedGoals.length > 0) {
      setPendingAction({ type: 'setSelectedPatient', value });
      setConfirmDialogOpen(true);
    } else {
      setSelectedPatient(value);
    }
  };

  const handleRadioToggle = (value) => {
    const newValue = value;
    if (selectedGoals.length > 0) {
      setPendingAction({
        type: 'setSelectedAssessmentType',
        value: newValue,
      });
      setConfirmDialogOpen(true);
    } else {
      setSelectedAssessmentType(newValue);
    }
  };

  const handleViewGoal = async (doc) => {
    const url = await generateAssessmentPdf(doc); 
    setPdfUrl(url);
    setPdfModalOpen(true);
  };

  const handleSelectGoal = (goal) => {
    const goalId = goal?.id;
    if (!goalId) return;

    const isAlreadySelected = selectedGoals.includes(goalId);

    if (isAlreadySelected) {
      setSelectedGoals((prev) => prev.filter((id) => id !== goalId));
    } else {
      setSelectedGoals((prev) => [...prev, goalId]);
    }
  };

  const handleConfirmReset = (pendingAction, selectedAssessmentType) => {
    if (pendingAction) {
      if (pendingAction.type === 'setSelectedAssessmentType') {
        setSelectedAssessmentType(pendingAction.value);
      } else if (pendingAction.type === 'setSelectedStudent') {
        setSelectedStudent(pendingAction.value);
      } else if (pendingAction.type === 'setSelectedPatient') {
        setSelectedPatient(pendingAction.value);
      } else if (pendingAction.type === 'setDateRange') {
        setDateRange(pendingAction.value);
      }

      setSelectedGoals([]);
      setPendingAction(null);
    }

    setConfirmDialogOpen(false);
  };

  const handleCancelReset = () => {
    setPendingAction(null);
    setConfirmDialogOpen(false);
  };

  return {
    handleDateChange,
    handleSelectStudent,
    handleSelectPatient,
    handleRadioToggle,
    handleViewGoal,
    handleSelectGoal,
    handleConfirmReset,
    handleCancelReset,
  };
};

export const handleGenerateReport = async ({
  selectedGoals,
  selectedStudent,
  user,
  filteredStudentGoals,
  shareWith,
  dateRange,
  setIsGenerating,
  setSelectedGoals,
  setShareWith,
  typeOfUser,
  ACM,
}) => {
  // For students/patients, we don't need selectedStudent validation
  const needsStudentSelection = ACM[typeOfUser]?.isTeacher || ACM[typeOfUser]?.isDoctor;
  
  if (selectedGoals.length === 0 || !user?.uid) {
    alert('Please select at least one goal.');
    return;
  }
  
  if (needsStudentSelection && !selectedStudent) {
    alert('Please select a student and at least one goal.');
    return;
  }

  setIsGenerating(true);
  const reportGroupId = uuidv4();

  try {
    const db = getFirestore();

    // Get full goal objects from filtered data
    // Handle both new data structure and legacy structure
    const allFilteredGoals = filteredStudentGoals.flatMap((student) => {
      if (student.studentData && !Array.isArray(student.studentData)) {
        // New data structure - single goal per student
        return [{ ...student.studentData, id: student.docId }];
      } else {
        // Legacy data structure - array of goals
        return student.dataCollection || [];
      }
    });

    const selectedGoalObjects = allFilteredGoals.filter((goal) =>
      selectedGoals.includes(goal.id)
    );

    const viewableByList = [...new Set([user.uid, ...shareWith])];

    // For students/patients, use their own ID as the student ID
    const studentId = needsStudentSelection ? selectedStudent : user.uid;

    const goalDocRef = collection(
      db,
      'reports',
      studentId,
      'progressreports'
    );

    for (const goal of selectedGoalObjects) {
      const encodedGoalInfo = btoa(JSON.stringify(goal));
      const goalDocData = {
        studentId: studentId,
        UploadedBy: user.uid,
        goalId: goal.id,
        info: encodedGoalInfo,
        dateRange: dateRange,
        shareWith: viewableByList,
        createdAt: serverTimestamp(),
        reportGroupId: reportGroupId,
        isEncrypted: false,
        reportType:"Progress-Report"
      };

      console.log('goalDocData', goalDocData);

       await addDoc(goalDocRef, goalDocData);
    }

    toast.success(
      'Progress report goals have been successfully created!',
      {
        position: toast.POSITION.BOTTOM_RIGHT,
      }
    );
    setSelectedGoals([]);
    setShareWith([]);

  } catch (error) {
    console.error('Error creating goal documents in progressreports:', error);
    alert('Failed to generate report. Please check the console for details.');
  } finally {
    setIsGenerating(false);
  }
}; 